import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getSubmissions,
    getSubmissionAnswers, downloadSubmission,
} from "../../redux/feedback/feedbackSlice";
import { truncateText } from "../../helper/helper";
import useDebounce from "../../hook/useDebounce";
import { useLocationFilter } from "../../context/LocationContext";
import PaginationHelper from "../../helper/PaginationHelper";

const FeedbackSubmissionLayer = () => {
    const dispatch = useDispatch();
    const { selectedLocation } = useLocationFilter();

    const { submissions = [], total = 0, answers = [] } = useSelector(
        (state) => state.feedback
    );

    const [entries, setEntries] = useState(50);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 1000);


    const [selectedSubmission, setSelectedSubmission] = useState(null);

    useEffect(() => {
        dispatch(getSubmissions({ page, limit: entries, search: debouncedSearch, locationId: selectedLocation?.value || "", }));
    }, [page, entries, debouncedSearch, selectedLocation]);

    const totalPages = Math.ceil(total / entries);

    const handleViewAnswers = (submission) => {
        setSelectedSubmission(submission);
        dispatch(getSubmissionAnswers(submission.id));
    };

    return (
        <>
            <div className="card">

                {/* HEADER */}
                <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">

                    <div className="d-flex gap-2">

                        <select
                            className="form-select w-auto"
                            value={entries}
                            onChange={(e) => {
                                setEntries(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="150">150</option>
                            <option value="200">200</option>
                        </select>

                        <input
                            className="form-control"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                    </div>

                    <div className="d-flex gap-2 align-items-center">

                        <button
                            className="btn btn-primary"
                            disabled={!selectedLocation}
                            onClick={() =>
                                dispatch(downloadSubmission(selectedLocation.value))
                            }
                        >
                            <i class="ri-file-excel-2-line"></i> Download
                        </button>

                    </div>

                </div>

                {/* TABLE */}
                <div className="card-body p-12">
                    <div className="table-responsive">

                        <table className="table bordered-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>User</th>
                                    <th>Email</th>
                                    <th>Event</th>
                                    <th>Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {submissions.length > 0 ? (
                                    submissions.map((item, i) => (
                                        <tr key={item.id}>

                                            <td>{(page - 1) * entries + i + 1}</td>

                                            <td>{item.user?.fullName}</td>
                                            <td>{item.user?.email}</td>
                                            <td>{truncateText(item.event?.name, 30)}</td>
                                            <td>{new Date(item.createdAt).toLocaleDateString()}</td>

                                            <td>
                                                <button
                                                    className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                    data-bs-toggle="modal"
                                                    data-bs-target="#answerModal"
                                                    onClick={() => handleViewAnswers(item)}
                                                >
                                                    <Icon icon="lucide:eye" />
                                                </button>
                                            </td>

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center">
                                            No feedback responses found
                                        </td>
                                    </tr>
                                )}

                            </tbody>

                        </table>
                    </div>

                    {/* PAGINATION */}
                    <PaginationHelper
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        totalItems={total}
                        pageSize={entries}
                    />

                </div>
            </div>

            {/* ANSWER MODAL */}
            <div className="modal fade" id="answerModal">
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">

                        <div className="modal-header">
                            <h5>Submission Answers</h5>
                            <button className="btn-close" data-bs-dismiss="modal" />
                        </div>

                        <div className="modal-body">

                            {/* USER INFO */}
                            {selectedSubmission && (
                                <div className="mb-3 p-3 border rounded">
                                    <strong>{selectedSubmission.user?.fullName}</strong>
                                    <div>{selectedSubmission.user?.email}</div>
                                    <div className="text-muted">
                                        {selectedSubmission.event?.name}
                                    </div>
                                </div>
                            )}

                            {/* ANSWERS */}
                            {answers.length === 0 ? (
                                <div className="text-muted">No answers found</div>
                            ) : (
                                <div className="d-flex flex-column gap-3">

                                    {answers.map((ans, i) => {

                                        const isMulti = ans.question?.type === "MULTISELECT";

                                        const parsedAnswers = isMulti
                                            ? ans.answer.split(",").map(a => a.trim())
                                            : [ans.answer];

                                        return (
                                            <div key={i} className="p-3 border rounded bg-light">

                                                <div className="fw-semibold mb-2">
                                                    {ans.question?.question}
                                                </div>

                                                <div>

                                                    {parsedAnswers.map((a, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="badge bg-success me-1 mb-1"
                                                        >
                                                            {a}
                                                        </span>
                                                    ))}

                                                </div>

                                            </div>
                                        );
                                    })}

                                </div>
                            )}

                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default FeedbackSubmissionLayer;